import type { Shop } from '../services/apiService';

// HOW A SHOP IS ADDRESSED IN A URL, AND WHY IT IS NOT `Shop.key`.
//
// `/feeds/:ref` has to survive a bookmark, a Dashboard link and a browser back
// button. `Shop.key` cannot: `shopsForUser` keys a feed `feed:<id>` only while it
// resolves to no single advertiser host, and `<network>:<programmeKey>` once it
// does — so the key CHANGES under the publisher, in three ordinary situations,
// all verified against the server:
//
//  1. A URL feed just connected has no resolved host, so it is keyed `feed:<id>`.
//     After its first sync it re-keys to `2performant:907`. "Connect it, then
//     open it" lands on a key that dies minutes later.
//
//  2. `Set advertiser` is offered ONLY on `feed:<id>` rows, and its entire
//     purpose is to make the next ingest resolve the host — which re-keys the
//     row. Succeeding at the action makes the page it was pressed on
//     unaddressable.
//
//  3. Programme rows expire after ENTITLEMENT_FRESHNESS_DAYS (7). A sweep
//     outage — which is what `reconnect_required` IS — re-keys every feed at
//     once, breaking every bookmark together, in the one incident a deep link
//     from the health banner exists for.
//
// So: a feed is addressed by its feed id, which nothing changes, and a shop
// without a feed by its programme key, which is stable for exactly those rows —
// subscription rows are listed regardless of entitlement freshness ("a
// subscription for a programme the entitlement sweep no longer reports still
// belongs on the page", `shop-list.ts`). Between them that is every row.
//
// Found by the Fable spec review before any of this was built. The first design
// keyed on `Shop.key` and would have shipped a deep link that breaks precisely
// when it is needed.

/** Path segments under `/feeds` that are screens, never shops. */
export const RESERVED_REFS = ['add', 'upload', 'connect'] as const;

/**
 * The address for a shop. Both forms carry a colon, so a caller putting one in
 * a path must encode it — `shopHref` does.
 */
export function shopRef(shop: Pick<Shop, 'feedId' | 'key'>): string {
  return shop.feedId !== null ? `feed:${shop.feedId}` : shop.key;
}

/** The href for a shop's room, encoded. */
export function shopHref(shop: Pick<Shop, 'feedId' | 'key'>): string {
  return `/feeds/${encodeURIComponent(shopRef(shop))}`;
}

/**
 * The shop an address names, or null.
 *
 * Feed id first, then key. The order matters: a row can be keyed
 * `2performant:907` AND have `feedId: 12`, and it is addressed `feed:12` — so a
 * `feed:` ref must never fall through to a key comparison that would miss it.
 */
export function findByRef(shops: Shop[], ref: string): Shop | null {
  const feedMatch = /^feed:(\d+)$/.exec(ref);
  if (feedMatch) {
    const id = Number(feedMatch[1]);
    return shops.find(s => s.feedId === id) ?? null;
  }
  return shops.find(s => s.key === ref) ?? null;
}

/** Is this path segment one of the screens rather than a shop? */
export function isReservedRef(ref: string): boolean {
  return (RESERVED_REFS as readonly string[]).includes(ref);
}
